import { useCallback, useEffect, useState } from 'react';
import {
  HOME3_CATEGORY_SURFACES,
  markVisited,
  readLastVisited,
  type Home3Category,
  type VisitedSurface,
} from './last-visited';
import { newSince } from './summary-freshness';

// The React half of the `new` badge: one hook that STAMPS a surface as visited, and one that reads
// the mark back for the hub tiles.

/**
 * Mark the surface visited — call it from the screen the reader actually opened (`/routines`,
 * `/tasks`, `/files`), not from the tile that links to it.
 */
export function useMarkVisited(surface: VisitedSurface): void {
  useEffect(() => {
    markVisited(surface);
  }, [surface]);
}

/**
 * The moment "new" starts from for this surface, **captured once at mount**, or `null` before the
 * first effect has run.
 *
 * Reading the stamp live would make the badge disappear underneath the reader: opening `/tasks`
 * writes a fresh stamp, and coming straight back to the hub would re-read it and drop the badge to
 * zero mid-render — which looks like a glitch rather than like "you've seen these now". Capturing at
 * mount means the badge holds still for as long as the reader is on the hub, and is recomputed the
 * next time they arrive.
 *
 * It starts `null` rather than computing during render for a second reason: the server has no
 * localStorage, so a value computed at render time would differ between the server's HTML and the
 * client's first paint and trip hydration. `null` renders no badge on both, and the badge appears on
 * the first client effect.
 */
export function useNewSince(surface: VisitedSurface): number | null {
  const [since, setSince] = useState<number | null>(null);
  useEffect(() => {
    setSince(newSince(readLastVisited(surface), Date.now()));
  }, [surface]);
  return since;
}

/**
 * The moment "new" starts from for a surface, **anchored per PAGE LOAD rather than per mount**.
 *
 * A screen that both reads the stamp and re-stamps it needs the value from BEFORE this visit, and
 * that value cannot live in React state. Two things overwrite it, and each one alone is enough to make
 * the count permanently zero:
 *
 *   1. **`reactStrictMode` double-invokes effects in development**, so a second read inside one mount
 *      sees the stamp the first pass wrote.
 *   2. **The screen remounts.** `useState` resets with it, so the next read gets the stamp the previous
 *      mount wrote. This is the one that actually bit: with the read guarded inside a single mount, a
 *      30-day-old planted stamp still rendered "0 new updates" in Chrome, and the instrumented value
 *      was the stamp from five seconds earlier.
 *
 * A module-level anchor has exactly the right lifetime. It is set by the first read after a page load
 * and survives every re-render, double-invoke and remount; a real navigation reloads the module and
 * the next visit re-anchors. It also gives the better behaviour on a client-side route away and back:
 * "since your last visit" keeps meaning the visit, rather than resetting to zero because the reader
 * clicked Home again.
 */
const visitAnchors = new Map<VisitedSurface, number>();

export function useVisitedOnce(surface: VisitedSurface): number | null {
  const [since, setSince] = useState<number | null>(null);
  useEffect(() => {
    let anchor = visitAnchors.get(surface);
    if (anchor === undefined) {
      // Read BEFORE marking — and only ever once per page load.
      anchor = newSince(readLastVisited(surface), Date.now());
      visitAnchors.set(surface, anchor);
    }
    setSince(anchor);
    markVisited(surface);
  }, [surface]);
  return since;
}

/** Drop the anchors. For tests only — a page load is the real reset. */
export function resetVisitAnchors(): void {
  visitAnchors.clear();
}

/**
 * The Overview home's per-category stamps, read together and marked one at a time.
 *
 * Each tile's disc counts what arrived in ITS category since that category was last pressed, so the
 * six are independent: pressing Tasks clears the Tasks disc and leaves the rest alone. The stamps are
 * namespaced away from the hub's own surfaces (see `Home3CategorySurface`) — opening the Tasks SCREEN
 * and pressing the Tasks TILE are different events and must not clear each other's badge.
 *
 * `marked` is React state rather than a re-read, so a press clears the disc on the spot: the store is
 * written synchronously but nothing re-renders off localStorage. It starts `null` (server, and the
 * first client paint) and resolves in an effect, which is what keeps the first render from claiming a
 * count it has not read.
 */
export function useHome3CategoryVisits(): {
  stamps: Record<Home3Category, number | null> | null;
  mark: (category: Home3Category) => void;
} {
  const [stamps, setStamps] = useState<Record<Home3Category, number | null> | null>(null);

  useEffect(() => {
    setStamps(
      Object.fromEntries(HOME3_CATEGORY_SURFACES.map((s) => [s.slice('home3:'.length), readLastVisited(s)])) as Record<
        Home3Category,
        number | null
      >,
    );
  }, []);

  const mark = useCallback((category: Home3Category) => {
    const now = Date.now();
    markVisited(`home3:${category}`, now);
    setStamps((current) => (current ? { ...current, [category]: now } : current));
  }, []);

  return { stamps, mark };
}
